/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useAppConfig } from '@airasia-rn/ui';
import { SettingsApiService } from '../services';
import { useAccessToken, useApiKey, useIsLoggedIn } from './account';
import { useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getValue, SettingData } from '../types';
import { Platform } from 'react-native';
import DeviceInfo from 'react-native-device-info';
import axios from 'axios';
import {
  loadStoredSettings,
  removeStoredSettings,
  saveStoredSettings,
  settingsStorageKey,
} from '../utils/settingsStorage';

/**
 * Query keys for settings-related React Query queries.
 *
 * Deliberately NOT under the `persist_cache` prefix: settings persistence is
 * owned by `settingsStorage` (IndexedDB on web, a dedicated AsyncStorage key
 * on native), so this query must not also ride the TanStack offline cache —
 * that would double-persist it and re-couple restarts to the persister.
 */
export const settingsKeys = {
  settings: (loggedIn: boolean, locale: string | undefined) => {
    return ['settings', loggedIn, locale];
  },
};

export interface SettingsState {
  settings: Record<string, SettingData>;
  etag: string | undefined;
  version: string;
}

function toNumber(str: string | undefined, fallback: number = 0): number {
  if (str == undefined) return fallback;
  const num = Number(str);
  return isNaN(num) ? fallback : num;
}

/** Matches Android `CoreManager.applicationVersion` (native build/version code). */
function getAppVersionCodeForFlags(
  marketingVersion: string | undefined,
): number {
  if (Platform.OS === 'web') {
    return toNumber(marketingVersion, 0);
  }

  try {
    const buildNumber = DeviceInfo.getBuildNumber()?.trim();
    const parsedBuildNumber = toNumber(buildNumber, Number.NaN);
    if (!Number.isNaN(parsedBuildNumber)) {
      return parsedBuildNumber;
    }
  } catch {
    // Fall back to marketing version when native build number is unavailable.
  }

  return toNumber(marketingVersion, 0);
}

/**
 * Helper function to create an instance of SettingsApiService.
 * Memoized based on app config values.
 */
export function useSettingsApiService(): SettingsApiService {
  const appConfig = useAppConfig();
  return useMemo(() => {
    return new SettingsApiService(
      appConfig.config.env,
      appConfig.config.appName,
      appConfig.config.appVersion,
    );
  }, [
    appConfig.config.appName,
    appConfig.config.appVersion,
    appConfig.config.env,
  ]);
}

/**
 * Hook to fetch settings from the API.
 * Uses React Query to cache and manage the settings state.
 * Returns settings as a Record<string, SettingData> for safer persistence.
 *
 * @param caller - Identifier of the caller for error logging purposes.
 */
export const useFetchSettings = () => {
  const api = useSettingsApiService();
  const queryClient = useQueryClient();
  const appConfig = useAppConfig();
  const apikeyQuery = useApiKey();
  const accessTokenQuery = useAccessToken('useFetchSettings');
  const isLoggedIn = useIsLoggedIn('useFetchSettings');
  const { locale } = appConfig.config;

  // Cache-first: surface the persisted snapshot the moment it can be read,
  // stamped with its REAL fetch time, instead of leaving every consumer on
  // `undefined` until the network (and the token gate ahead of it) lets the
  // fetch resolve. TanStack's staleness rules then do the right thing: an old
  // snapshot renders instantly and still revalidates in the background; the
  // resolving fetch always overwrites the seed. This mirrors what
  // persistQueryClient's restore used to provide, scoped to this one query.
  useEffect(() => {
    let cancelled = false;
    const key = settingsKeys.settings(isLoggedIn.isLoggedIn, locale);
    void loadStoredSettings(settingsStorageKey(isLoggedIn.isLoggedIn)).then(
      (entry) => {
        if (cancelled || !entry) return;
        // Live data (a resolved fetch, or an earlier seed) always wins.
        if ((queryClient.getQueryState(key)?.dataUpdatedAt ?? 0) > 0) return;
        queryClient.setQueryData<SettingsState>(key, entry.state, {
          updatedAt: entry.storedAt,
        });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [queryClient, isLoggedIn.isLoggedIn, locale]);

  return useQuery<SettingsState>({
    queryKey: settingsKeys.settings(isLoggedIn.isLoggedIn, locale),
    staleTime: (appConfig.config.env === 'stg' ? 10 : 2 * 60) * 1000, // 30 seconds in stg and  2 min in prod
    queryFn: async (): Promise<SettingsState> => {
      const key = settingsKeys.settings(isLoggedIn.isLoggedIn, locale);
      const storageKey = settingsStorageKey(isLoggedIn.isLoggedIn);
      // Warm memory first; fall back to the platform store so the etag (and
      // any local overrides) survive restarts without the query persister.
      const currentSettings =
        queryClient.getQueryData<SettingsState>(key) ??
        (await loadStoredSettings(storageKey))?.state;
      const etag = currentSettings?.etag;

      let token;
      if (isLoggedIn.isLoggedIn) {
        token = await accessTokenQuery.getToken();
      } else {
        token = (await apikeyQuery.getToken())?.apiKey;
      }

      try {
        const res = await api.fetchSettings(token ?? 'NA', etag, 'MY', locale);

        const settingsObj: Record<string, SettingData> = {};

        const settingsMap = new Map<string, string>(
          Object.entries(res.data.settings),
        );
        settingsMap.forEach((value, key) => {
          settingsObj[key] = {
            key,
            value,
            localValue: currentSettings?.settings?.[key]?.localValue,
          } as SettingData;
        });

        const nextState: SettingsState = {
          settings: settingsObj,
          version: res.data.version,
          etag: res.headers['Etag'] || res.headers['etag'],
        };
        // Fire-and-forget: storage is a cache, the response must not wait on it.
        void saveStoredSettings(storageKey, nextState);
        return nextState;
      } catch (error) {
        if (axios.isAxiosError(error)) {
          // Check for Axios standard error structure or a custom HTTP error property
          const status = error?.response?.status || error?.status;

          if (status === 304) {
            return currentSettings ?? { settings: {}, version: '', etag: '' }; // Content not modified
          }
        }

        throw error;
      }
    },
    // refetchOnMount: false, // monitor api calls on web and adjust this flag accordingly
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    enabled:
      apikeyQuery.isTokenValid &&
      (!isLoggedIn.isLoggedIn ||
        (isLoggedIn.isSuccess && accessTokenQuery.isTokenValid)),
  });
};

/**
 * Hook to retrieve settings, optionally filtered by a search key.
 * Returns settings as a Record<string, SettingData> for safer persistence and easier usage.
 *
 * @param searchKey - Optional string to filter settings by key.
 */
export const useSettings = (searchKey: string | undefined = undefined) => {
  const settingsQuery = useFetchSettings();

  const filteredSettings = useMemo(() => {
    const rawSettings = settingsQuery.data?.settings;
    if (!rawSettings) return {};

    // rawSettings is already a Record<string, SettingData>
    const settingsObj = rawSettings;

    if (!searchKey || searchKey === '') return settingsObj;

    const filtered: Record<string, SettingData> = {};
    Object.entries(settingsObj).forEach(([key, value]) => {
      if (key.toLowerCase().includes(searchKey.toLowerCase())) {
        filtered[key] = value;
      }
    });
    return filtered;
  }, [settingsQuery.data, searchKey]);

  return {
    settings: filteredSettings,
    isSettingsLoading: settingsQuery.isLoading,
    error: settingsQuery.error,
  };
};

/**
 * Hook to retrieve a single setting value by key.
 *
 * @param key - The key of the setting to retrieve.
 */
export const useSetting = (key: string) => {
  const { data, isLoading } = useFetchSettings();

  const value = useMemo<string | undefined>(() => {
    if (!data) return undefined;
    return getValue(data.settings[key]);
  }, [data, key]);

  return useMemo(
    () => ({
      value,
      isSettingsLoading: isLoading,
    }),
    [value, isLoading],
  );
};

/**
 * Hook to check if a feature flag is enabled.
 * Compares the flag value (version number) with the current app version.
 *
 * @param flag - The key of the feature flag setting.
 */
export const useFlagEnabled = (
  flag: string,
  defaultEnabled: boolean = false,
) => {
  const setting = useSetting(flag);
  const appConfig = useAppConfig();

  const enabled = useMemo<boolean>(() => {
    const appVersion = getAppVersionCodeForFlags(appConfig.config.appVersion);
    const enableVersion = toNumber(setting.value, defaultEnabled ? 1 : -1);
    if (Platform.OS == 'web') return enableVersion > 0;
    else return enableVersion > 0 && appVersion >= enableVersion;
  }, [appConfig.config.appVersion, defaultEnabled, setting.value]);

  return {
    enabled,
    isSettingsLoading: setting.isSettingsLoading,
  };
};

/**
 * Mutation hook to update a local setting override.
 * Updates the cache directly for immediate UI feedback.
 */
export const useUpdateLocalSetting = () => {
  const queryClient = useQueryClient();
  const appConfig = useAppConfig();
  const isLoggedIn = useIsLoggedIn('useUpdateLocalSetting');
  const { locale } = appConfig.config;

  return useMutation({
    mutationFn: async ({ key, value }: { key: string; value: string }) => {
      const queryKey = settingsKeys.settings(isLoggedIn.isLoggedIn, locale);
      const currentSettings = queryClient.getQueryData<SettingsState>(queryKey);

      if (currentSettings) {
        const newSettings = { ...currentSettings.settings };
        const setting = newSettings[key];
        if (setting) {
          newSettings[key] = { ...setting, localValue: value };
          const nextState: SettingsState = {
            ...currentSettings,
            settings: newSettings,
          };
          queryClient.setQueryData<SettingsState>(queryKey, nextState);
          // The offline cache used to pick this up implicitly; now the
          // override is written to the platform store explicitly.
          void saveStoredSettings(
            settingsStorageKey(isLoggedIn.isLoggedIn),
            nextState,
          );
        }
      }
    },
  });
};

/**
 * Mutation hook to reset all local setting overrides.
 * Updates the cache directly for immediate UI feedback.
 */
export const useResetLocalSettings = () => {
  const queryClient = useQueryClient();
  const appConfig = useAppConfig();
  const isLoggedIn = useIsLoggedIn('useResetLocalSettings');
  const { locale } = appConfig.config;

  return useMutation({
    mutationFn: async () => {
      const queryKey = settingsKeys.settings(isLoggedIn.isLoggedIn, locale);
      queryClient.removeQueries({ queryKey: queryKey });
      // Without this the next queryFn would resurrect the dropped overrides
      // from the platform store.
      await removeStoredSettings(settingsStorageKey(isLoggedIn.isLoggedIn));
    },
  });
};
